import { changeSignatureFromFirstRank } from "@/state/transactions/CurrentTransactionEvents";
import BasicButton from "@components/core/buttons/BasicButton";
import InfoModal from "@components/core/modals/InfoModal";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import { useTranslation } from "react-i18next";
import React, { Dispatch, SetStateAction, useState } from "react";
import { Member } from "@type/core/offices/offices.types";
import { Signatory, Signature } from "@type/core/dashboard/dashboard.types";
import { TransactionType } from "@type/core/transaction/transaction.types";
import Signatories from "@components/core/pdf/rightpanel/Signatories";
import ScellementSignatory from "@components/core/pdf/rightpanel/ScellementSignatory";
import SignatureHelper from "@utils/helpers/signature-helper";
import { MAX_SIGNATURE_AMOUNT } from "@utils/Constant";

interface RightPanelSignaturesProps {
  src: string | undefined;
  loading: boolean;
  index: number;
  signatories: Signatory[];
  signatures: Signature[];
  transactionType: TransactionType;
  members: Member[];
  setManualPositioning: Dispatch<SetStateAction<boolean>>;
  filename: string;
  officeId: string;
  pageNumber: number;
  numberOfFiles: number;
  buttonLoading?: boolean;
}

const RightPanelSignatures: React.FC<RightPanelSignaturesProps> = ({
  src,
  index,
  signatories,
  transactionType,
  signatures,
  members,
  officeId,
  setManualPositioning,
  pageNumber,
  numberOfFiles,
  buttonLoading,
  filename,
}: RightPanelSignaturesProps) => {
  const { t } = useTranslation();
  const [positioningModalVisible, setPositioningModalVisible] =
    useState<boolean>(false);
  const handleApplySignaturePositionToEveryFiles = () => {
    changeSignatureFromFirstRank();
    setPositioningModalVisible(false);
  };

  return (
    <>
      {transactionType === "signature" && (
        <span className="pdf-container-titles">
          {t("signature.positioning.signatures")}
        </span>
      )}
      <div
        title={
          transactionType === "signature"
            ? t("signature.positioning.place", { count: MAX_SIGNATURE_AMOUNT })
            : t("scellement.positioning.place")
        }
        className="text-nowrap h-25px"
      >
        <span className="d-none d-md-inline">
          {transactionType === "signature"
            ? t("signature.positioning.place", { count: MAX_SIGNATURE_AMOUNT })
            : t("scellement.positioning.place")}
        </span>
        {}
        <span className="d-md-none">{t("signature.signatures")}</span>
      </div>
      <div className="d-none d-md-inline text-tertiary">
        {transactionType === "signature" &&
        SignatureHelper.shouldSignaturesBeDisabled(signatures, index)
          ? t("signature.positioning.limitExceed", {
              count: MAX_SIGNATURE_AMOUNT,
            })
          : ""}
        &nbsp;
      </div>
      {transactionType === "signature" && (
        <Signatories
          src={src}
          index={index}
          signatories={signatories}
          filename={filename}
          signatures={signatures}
          transactionType={transactionType}
          members={members}
          setManualPositioning={setManualPositioning}
          officeId={officeId}
          pageNumber={pageNumber}
          numberOfFiles={numberOfFiles}
        />
      )}
      {!!(transactionType === "scellement" && src) && (
        <ScellementSignatory
          index={index}
          signatures={signatures}
          transactionType={transactionType}
          setManualPositioning={setManualPositioning}
          officeId={officeId}
          pageNumber={pageNumber}
        />
      )}
      {index === 1 && numberOfFiles > 1 && transactionType === "signature" && (
        <div className="d-flex mt-32 align-items-center">
          <BasicButton
            disabled={buttonLoading}
            isLoading={buttonLoading}
            variant="tertiary"
            text={t("signature.positioning.applyFirstToOthers")}
            className="mw-300 mr-4 py-8 px-30 mh-42 font-extra-small"
            onClick={() => setPositioningModalVisible(true)}
          />
          <InfoModal className="ml-8" value="positioning" info />
          <ConfirmModal
            title={t("signature.positioning.manualBody")}
            visible={positioningModalVisible}
            onOk={handleApplySignaturePositionToEveryFiles}
            onCancel={() => setPositioningModalVisible(false)}
          />
        </div>
      )}
    </>
  );
};

export default RightPanelSignatures;
